var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useAppSelector } from 'hooks/reduxHooks';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Button, Menu, MenuItem, } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import clsx from 'clsx';
import { InputFields } from 'common/InputFields';
import { WellnessLeftPanelStyle } from './WellnessLeftPanel.style';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
var SPECIAL_CHAR_REGEX = /^[a-zA-Z0-9\s.]*$/;
var SPECIAL_CHAR_MESSAGE = 'Special characters are not allowed';
var WellnessLeftPanel = React.memo(function (_a) {
    var _b, _c, _d, _e;
    var data = _a.data, setData = _a.setData, collapseAfterSave = _a.collapseAfterSave, templates = _a.templates, onTemplateSelect = _a.onTemplateSelect, readOnly = _a.readOnly, forceExpanded = _a.forceExpanded;
    var classes = WellnessLeftPanelStyle().classes;
    var isLatestVersion = useAppSelector(function (state) { return state.wellnessPlan; }).isLatestVersion;
    var isDataEmpty = !((_b = data === null || data === void 0 ? void 0 : data.situation) === null || _b === void 0 ? void 0 : _b.value) &&
        !((_c = data === null || data === void 0 ? void 0 : data.background) === null || _c === void 0 ? void 0 : _c.value) &&
        !((_d = data === null || data === void 0 ? void 0 : data.assessment) === null || _d === void 0 ? void 0 : _d.value) &&
        !((_e = data === null || data === void 0 ? void 0 : data.recommendation) === null || _e === void 0 ? void 0 : _e.value);
    var _f = React.useState(isDataEmpty), expanded = _f[0], setExpanded = _f[1];
    var _g = React.useState(null), anchorEl = _g[0], setAnchorEl = _g[1];
    var handleTemplateMenu = function (event) {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };
    var handleTemplateClose = function () {
        setAnchorEl(null);
    };
    var open = Boolean(anchorEl);
    var handleChange = function (key, e) {
        var value = e.target.value;
        var hasInvalidCharacters = !!value && !SPECIAL_CHAR_REGEX.test(value);
        setData(function (prevState) {
            var _a;
            return (__assign(__assign({}, prevState), (_a = {}, _a[key] = __assign(__assign({}, prevState[key]), { value: value, error: hasInvalidCharacters, errorText: hasInvalidCharacters ? SPECIAL_CHAR_MESSAGE : '' }), _a)));
        });
    };
    React.useEffect(function () {
        if (collapseAfterSave > 0) {
            setExpanded(false);
        }
    }, [collapseAfterSave]);
    var isExpanded = forceExpanded || expanded;
    return (_jsx(Box, { mt: 3, children: _jsxs(Accordion, { expanded: isExpanded, onChange: function (_, isExpanded) {
                if (forceExpanded)
                    return;
                if (!isDataEmpty) {
                    setExpanded(isExpanded);
                }
            }, style: {
                borderRadius: '15px',
            }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, {}), children: _jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 2, children: [_jsx(Typography, { variant: 'h6', fontWeight: 600, children: "SBAR Details" }), _jsx(Button, { variant: 'contained', disabled: readOnly, startIcon: _jsx(DescriptionOutlinedIcon, {}), endIcon: _jsx(KeyboardArrowDownIcon, {}), onClick: handleTemplateMenu, children: "Templates" })] }) }), _jsxs(AccordionDetails, { children: [_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(RenderField, { stateKey: 'situation', label: 'Situation', placeholder: 'Please enter situation here', isError: data.situation.error, errorText: data.situation.errorText, handleChange: handleChange, value: data.situation.value, disabled: readOnly || !isLatestVersion }), _jsx(RenderField, { stateKey: 'background', label: 'Background', placeholder: 'Please enter background here', isError: data.background.error, errorText: data.background.errorText, handleChange: handleChange, value: data.background.value, disabled: readOnly || !isLatestVersion }), _jsx(RenderField, { stateKey: 'assessment', label: 'Assessment', placeholder: 'Please enter assessment here', isError: data.assessment.error, errorText: data.assessment.errorText, handleChange: handleChange, value: data.assessment.value, disabled: readOnly || !isLatestVersion }), _jsx(RenderField, { stateKey: 'recommendation', label: 'Recommendation', placeholder: 'Please enter recommendation here', isError: data.recommendation.error, errorText: data.recommendation.errorText, handleChange: handleChange, value: data.recommendation.value, disabled: readOnly || !isLatestVersion })] }), _jsx(Menu, { anchorEl: anchorEl, open: open, onClose: handleTemplateClose, PaperProps: {
                                sx: {
                                    width: 'fit-content',
                                    mt: 1,
                                    borderRadius: 2,
                                    boxShadow: '0px 4px 20px rgba(0,0,0,0.15)',
                                    p: 1,
                                },
                            }, children: templates.length ? (__spreadArray([], templates, true).sort(function (a, b) {
                                return (a.template_name || '').localeCompare(b.template_name || '', undefined, { sensitivity: 'base' });
                            })
                                .map(function (template) { return (_jsx(MenuItem, { onClick: function () {
                                    handleTemplateClose();
                                    onTemplateSelect(template);
                                }, children: template.template_name }, template.template_id)); })) : (_jsx(MenuItem, { disabled: true, sx: { justifyContent: 'center' }, children: "No Templates Found" })) })] })] }) }));
});
var RenderField = React.memo(function (_a) {
    var _b;
    var stateKey = _a.stateKey, label = _a.label, placeholder = _a.placeholder, isError = _a.isError, errorText = _a.errorText, handleChange = _a.handleChange, value = _a.value, disabled = _a.disabled;
    var classes = WellnessLeftPanelStyle().classes;
    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', children: [_jsxs(Typography, { component: "div", className: clsx(classes.fieldsHeading, (_b = {},
                    _b[classes.errorText] = isError,
                    _b)), variant: 'h6', style: { fontWeight: 500 }, children: [label, _jsx(Box, { component: 'span', className: classes.errorText, children: "*" })] }), _jsx(InputFields, { isError: isError, errorText: errorText, eventProps: {
                    onChange: function (e) { return handleChange(stateKey, e); },
                    value: value || '',
                    disabled: disabled,
                }, inputProps: {
                    name: stateKey,
                    placeholder: placeholder,
                    required: true,
                    maxLength: 2000,
                    style: { padding: 0 },
                }, multiline: true, rows: 4 })] }));
});
export default WellnessLeftPanel;
